import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ArrowUp,
  ArrowDown,
  Square,
} from "lucide-react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { usePermissions, useHasPermission } from "@/contexts/PermissionContext";
import { buildHierarchyTree } from "@/utils/componentHierarchy";
import type { HierarchyNode } from "@/utils/componentHierarchy";
import { ComponentType } from "@/types";
import type { ComponentUpdateWithBreakpoint } from "@/types";
import {
  ComponentPermissionType,
  PermissionAction,
  ResourceType,
} from "@/types/permissions";
import { cn } from "@/lib/utils";
import { useParams } from "react-router-dom";
import { componentDefinitions } from "./ComponentPaletteSimple";
import {
  getViewerPermissionIdString,
  getViewerPermissionIdStrings,
} from "@/components/permissions/viewerPermissionIds";

function getTypeIcon(
  type: ComponentType,
): React.ComponentType<{ className?: string }> {
  return (
    componentDefinitions.find((definition) => definition.type === type)?.icon ??
    Square
  );
}

const EDITOR_PROJECT_ROLES = ["owner", "admin", "editor"];

function collectExpandableIds(nodes: HierarchyNode[]): string[] {
  const ids: string[] = [];

  nodes.forEach((node) => {
    if (node.component.isSubComponent === true) return;

    const visibleChildren = getVisibleLayerChildren(node);
    if (visibleChildren.length > 0) {
      ids.push(node.component.id);
      ids.push(...collectExpandableIds(visibleChildren));
    }
  });

  return ids;
}

function haveSameIds(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;

  for (const id of a) {
    if (!b.has(id)) return false;
  }

  return true;
}

function getVisibleLayerChildren(node: HierarchyNode): HierarchyNode[] {
  // Registry-created sub-components are virtual leaves, so filtering them cannot hide user-visible descendants.
  return node.children.filter(isVisibleLayerNode);
}

function isVisibleLayerNode(node: HierarchyNode): boolean {
  return node.component.isSubComponent !== true;
}

interface NodeProps {
  node: HierarchyNode;
  previousVisibleSiblingId?: string;
  nextVisibleSiblingId?: string;
  expandedIds: Set<string>;
  toggleExpand: (id: string) => void;
  selectedComponents: string[];
  selectComponent: (id: string) => void;
  toggleComponentSelection: (id: string) => void;
  updateComponent: (id: string, updates: ComponentUpdateWithBreakpoint) => void;
  moveComponentBefore: (componentId: string, targetId: string) => void;
  moveComponentAfter: (componentId: string, targetId: string) => void;
  canEditComponent: (id: string) => boolean;
}

function LayerTreeNode({
  node,
  previousVisibleSiblingId,
  nextVisibleSiblingId,
  expandedIds,
  toggleExpand,
  selectedComponents,
  selectComponent,
  toggleComponentSelection,
  updateComponent,
  moveComponentBefore,
  moveComponentAfter,
  canEditComponent,
}: NodeProps) {
  const { component } = node;

  // isSubComponent nodes are virtual editor-only nodes not meaningful to users
  if (component.isSubComponent === true) return null;

  const visibleChildren = getVisibleLayerChildren(node);
  const isSelected = selectedComponents.includes(component.id);
  const hasChildren = visibleChildren.length > 0;
  const isExpanded = expandedIds.has(component.id);
  const canEdit = canEditComponent(component.id);
  const canEditParent =
    component.parentId === undefined || canEditComponent(component.parentId);
  const canMoveBefore =
    previousVisibleSiblingId !== undefined &&
    canEdit &&
    canEditParent &&
    canEditComponent(previousVisibleSiblingId);
  const canMoveAfter =
    nextVisibleSiblingId !== undefined &&
    canEdit &&
    canEditParent &&
    canEditComponent(nextVisibleSiblingId);
  const Icon = getTypeIcon(component.type);
  const visibilityLabel = component.hidden
    ? "Show component"
    : "Hide component";
  const lockLabel = component.locked ? "Unlock component" : "Lock component";
  const controlButtonClassName = cn(
    "shrink-0 h-5 w-5 flex items-center justify-center rounded",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    canEdit
      ? "text-muted-foreground hover:text-foreground"
      : "cursor-not-allowed text-muted-foreground/50 opacity-50 hover:text-muted-foreground/50 hover:bg-transparent",
  );
  const visibilityIconClassName = cn(
    "h-3 w-3",
    canEdit
      ? component.hidden
        ? "opacity-40"
        : "text-foreground opacity-100"
      : "text-muted-foreground opacity-100",
  );
  const lockIconClassName = cn(
    "h-3 w-3",
    canEdit
      ? component.locked
        ? "text-foreground opacity-100"
        : "opacity-40"
      : "text-muted-foreground opacity-100",
  );
  const moveUpLabel = "Move component up";
  const moveDownLabel = "Move component down";

  return (
    <div>
      <div
        role="row"
        aria-selected={isSelected}
        className={cn(
          "flex items-center gap-1 h-7 rounded-sm cursor-pointer group select-none",
          "hover:bg-accent",
          isSelected && "bg-accent",
        )}
        style={{ paddingLeft: `${8 + node.depth * 16}px`, paddingRight: "8px" }}
        onClick={(e) => {
          if (e.shiftKey) {
            toggleComponentSelection(component.id);
          } else if (!isSelected) {
            selectComponent(component.id);
          }
        }}
      >
        <span
          className="shrink-0 h-4 w-4 flex items-center justify-center text-muted-foreground"
          onClick={(e) => {
            if (!hasChildren) return;
            e.stopPropagation();
            toggleExpand(component.id);
          }}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            ))}
        </span>

        <Icon
          className={cn(
            "shrink-0 h-3.5 w-3.5 text-muted-foreground",
            component.hidden && "opacity-40",
          )}
        />

        <span
          className={cn(
            "flex-1 truncate text-xs min-w-0 text-foreground",
            component.hidden && "opacity-40",
          )}
        >
          {component.name || component.type}
        </span>

        <button
          type="button"
          aria-disabled={!canMoveBefore}
          disabled={!canMoveBefore}
          tabIndex={canMoveBefore ? 0 : -1}
          className={cn(
            controlButtonClassName,
            !canMoveBefore &&
              "cursor-not-allowed text-muted-foreground/50 opacity-50 hover:text-muted-foreground/50 hover:bg-transparent",
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (!canMoveBefore || previousVisibleSiblingId === undefined) {
              return;
            }
            moveComponentBefore(component.id, previousVisibleSiblingId);
          }}
          aria-label={
            canMoveBefore
              ? moveUpLabel
              : `${moveUpLabel} unavailable for this layer`
          }
          title={canMoveBefore ? moveUpLabel : "Cannot move component up"}
        >
          <ArrowUp className="h-3 w-3" />
        </button>

        <button
          type="button"
          aria-disabled={!canMoveAfter}
          disabled={!canMoveAfter}
          tabIndex={canMoveAfter ? 0 : -1}
          className={cn(
            controlButtonClassName,
            !canMoveAfter &&
              "cursor-not-allowed text-muted-foreground/50 opacity-50 hover:text-muted-foreground/50 hover:bg-transparent",
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (!canMoveAfter || nextVisibleSiblingId === undefined) {
              return;
            }
            moveComponentAfter(component.id, nextVisibleSiblingId);
          }}
          aria-label={
            canMoveAfter
              ? moveDownLabel
              : `${moveDownLabel} unavailable for this layer`
          }
          title={canMoveAfter ? moveDownLabel : "Cannot move component down"}
        >
          <ArrowDown className="h-3 w-3" />
        </button>

        <button
          type="button"
          aria-disabled={!canEdit}
          disabled={!canEdit}
          tabIndex={canEdit ? 0 : -1}
          className={controlButtonClassName}
          onClick={(e) => {
            e.stopPropagation();
            if (!canEdit) return;
            updateComponent(component.id, { hidden: !component.hidden });
          }}
          aria-label={
            canEdit
              ? visibilityLabel
              : `${visibilityLabel} unavailable without edit permission`
          }
          title={canEdit ? visibilityLabel : "Edit permission required"}
        >
          {component.hidden ? (
            <EyeOff className={visibilityIconClassName} />
          ) : (
            <Eye className={visibilityIconClassName} />
          )}
        </button>

        <button
          type="button"
          aria-disabled={!canEdit}
          disabled={!canEdit}
          tabIndex={canEdit ? 0 : -1}
          className={controlButtonClassName}
          onClick={(e) => {
            e.stopPropagation();
            if (!canEdit) return;
            updateComponent(component.id, { locked: !component.locked });
          }}
          aria-label={
            canEdit
              ? lockLabel
              : `${lockLabel} unavailable without edit permission`
          }
          title={canEdit ? lockLabel : "Edit permission required"}
        >
          {component.locked ? (
            <Lock className={lockIconClassName} />
          ) : (
            <Unlock className={lockIconClassName} />
          )}
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div role="rowgroup">
          {visibleChildren.map((child, index) => (
            <LayerTreeNode
              key={child.component.id}
              node={child}
              previousVisibleSiblingId={
                visibleChildren[index - 1]?.component.id
              }
              nextVisibleSiblingId={visibleChildren[index + 1]?.component.id}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
              selectedComponents={selectedComponents}
              selectComponent={selectComponent}
              toggleComponentSelection={toggleComponentSelection}
              updateComponent={updateComponent}
              moveComponentBefore={moveComponentBefore}
              moveComponentAfter={moveComponentAfter}
              canEditComponent={canEditComponent}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const LayerTreePanel: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const components = useEditorStore((s) => s.components);
  const selectedComponents = useEditorStore((s) => s.selectedComponents);
  const selectComponent = useEditorStore((s) => s.selectComponent);
  const toggleComponentSelection = useEditorStore(
    (s) => s.toggleComponentSelection,
  );
  const updateComponent = useEditorStore((s) => s.updateComponent);
  const moveComponentBefore = useEditorStore((s) => s.moveComponentBefore);
  const moveComponentAfter = useEditorStore((s) => s.moveComponentAfter);
  const project = useEditorStore((s) => s.project);
  const currentPageId = useEditorStore((s) => s.currentPageId);
  const user = useAuthStore((s) => s.user);
  const { getComponentPermissions } = usePermissions();

  const tree = useMemo(() => buildHierarchyTree(components), [components]);
  const visibleRootNodes = useMemo(
    () => tree.filter(isVisibleLayerNode),
    [tree],
  );
  const expandableIds = useMemo(() => collectExpandableIds(tree), [tree]);
  const pageExpansionKey = `${project?.id ?? "no-project"}:${currentPageId ?? "no-page"}`;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(expandableIds),
  );
  const knownContainerIdsRef = useRef<Set<string>>(new Set(expandableIds));
  const pageExpansionKeyRef = useRef(pageExpansionKey);

  const currentUserIds = useMemo(
    () => getViewerPermissionIdStrings(user?.id, user?._id),
    [user?.id, user?._id],
  );
  const projectOwnerIdStr =
    getViewerPermissionIdString(project?.ownerId) ??
    getViewerPermissionIdString(project?.owner_id);
  const isProjectOwner = Boolean(
    projectOwnerIdStr && currentUserIds.has(projectOwnerIdStr),
  );
  const userMembership = project?.members?.find((member) => {
    const memberUserIdStr = getViewerPermissionIdString(member.userId);
    return Boolean(memberUserIdStr && currentUserIds.has(memberUserIdStr));
  });
  const hasProjectEditAccess = Boolean(
    isProjectOwner ||
      (userMembership && EDITOR_PROJECT_ROLES.includes(userMembership.role)),
  );
  const permissionProjectId = projectId ?? project?.id ?? "";
  const apiCanEditProject = useHasPermission(
    { type: ResourceType.PROJECT, id: permissionProjectId },
    PermissionAction.UPDATE,
  );
  const canEditProject = apiCanEditProject || hasProjectEditAccess;

  const projectPermissionContext = useMemo(
    () => ({
      ownerId: project?.ownerId,
      owner_id: project?.owner_id,
      members: project?.members,
    }),
    [project?.ownerId, project?.owner_id, project?.members],
  );

  const canEditComponent = useCallback(
    (componentId: string) => {
      const componentPermissions = getComponentPermissions(
        componentId,
        projectPermissionContext,
      );

      if (componentPermissions) {
        return (
          componentPermissions[ComponentPermissionType.UPDATE]?.allowed !==
          false
        );
      }

      return canEditProject;
    },
    [canEditProject, getComponentPermissions, projectPermissionContext],
  );

  useEffect(() => {
    const nextContainerIds = new Set(expandableIds);

    if (pageExpansionKeyRef.current !== pageExpansionKey) {
      pageExpansionKeyRef.current = pageExpansionKey;
      knownContainerIdsRef.current = nextContainerIds;
      setExpandedIds(nextContainerIds);
      return;
    }

    const previousKnownIds = knownContainerIdsRef.current;
    const newlyDiscoveredIds = expandableIds.filter(
      (id) => !previousKnownIds.has(id),
    );
    knownContainerIdsRef.current = nextContainerIds;

    setExpandedIds((previousExpandedIds) => {
      const nextExpandedIds = new Set<string>();

      previousExpandedIds.forEach((id) => {
        if (nextContainerIds.has(id)) {
          nextExpandedIds.add(id);
        }
      });
      newlyDiscoveredIds.forEach((id) => nextExpandedIds.add(id));

      return haveSameIds(previousExpandedIds, nextExpandedIds)
        ? previousExpandedIds
        : nextExpandedIds;
    });
  }, [expandableIds, pageExpansionKey]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <div
      className="h-full overflow-y-auto py-1 bg-card"
      role="treegrid"
      aria-label="Component layers"
    >
      {visibleRootNodes.length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">
          No components on this page.
        </p>
      ) : (
        visibleRootNodes.map((node, index) => (
          <LayerTreeNode
            key={node.component.id}
            node={node}
            previousVisibleSiblingId={visibleRootNodes[index - 1]?.component.id}
            nextVisibleSiblingId={visibleRootNodes[index + 1]?.component.id}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
            selectedComponents={selectedComponents}
            selectComponent={selectComponent}
            toggleComponentSelection={toggleComponentSelection}
            updateComponent={updateComponent}
            moveComponentBefore={moveComponentBefore}
            moveComponentAfter={moveComponentAfter}
            canEditComponent={canEditComponent}
          />
        ))
      )}
    </div>
  );
};
